import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { LEAD_STATUS_LABEL, LEAD_STATUSES } from "@/lib/i18n";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Save, Undo2 } from "lucide-react";

const EMPLOYMENT_OPTIONS = ["Salaried", "Self-employed", "Business owner", "Freelancer", "Retired", "Other"];

// Business-side fields the advisor edits. Server-driven fields (name/email/phone/status/notes/source)
// stay separate so this form never accidentally wipes them.
const EDITABLE_FIELDS = [
  "interest", "budget_aed", "loan_amount_aed", "loan_tenure_years",
  "monthly_income_aed", "employment_type", "country",
];

function pickEditable(lead) {
  const out = {};
  EDITABLE_FIELDS.forEach((k) => { out[k] = lead?.[k] ?? ""; });
  return out;
}

export default function AdminLeadDetail() {
  const { leadId } = useParams();
  const { t, lang, isRTL } = useLang();
  const [lead, setLead] = useState(null);
  const [loadErr, setLoadErr] = useState("");
  const [note, setNote] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [form, setForm] = useState(pickEditable(null));
  const [savingLead, setSavingLead] = useState(false);

  const load = async () => {
    try {
      const r = await api.get(`/leads/${leadId}`);
      setLead(r.data);
      setForm(pickEditable(r.data));
      setLoadErr("");
    } catch (e) {
      setLoadErr(e?.response?.data?.detail || "Could not load lead.");
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [leadId]);

  const dirty = useMemo(() => {
    if (!lead) return false;
    return EDITABLE_FIELDS.some((k) => {
      const orig = lead[k] ?? "";
      const now = form[k] ?? "";
      return String(orig) !== String(now);
    });
  }, [lead, form]);

  const setStatus = async (status) => {
    try {
      const r = await api.patch(`/leads/${leadId}/status`, { status });
      setLead(r.data);
      toast.success("Status updated.");
    } catch { toast.error("Update failed."); }
  };

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const revert = () => setForm(pickEditable(lead));

  const saveLead = async () => {
    if (!dirty || savingLead) return;
    setSavingLead(true);
    // Build payload: only send changed fields. Coerce numeric fields.
    const payload = {};
    for (const k of EDITABLE_FIELDS) {
      const orig = lead?.[k] ?? "";
      const raw = form[k];
      if (String(orig) === String(raw ?? "")) continue;
      if (raw === "" || raw === null || raw === undefined) {
        payload[k] = "";
        continue;
      }
      if (k === "budget_aed" || k === "loan_amount_aed" || k === "monthly_income_aed") {
        const n = Number(String(raw).replace(/[^0-9.]/g, ""));
        payload[k] = Number.isFinite(n) ? n : "";
      } else if (k === "loan_tenure_years") {
        const n = parseInt(String(raw).replace(/\D/g, ""), 10);
        payload[k] = Number.isFinite(n) ? n : "";
      } else {
        payload[k] = raw;
      }
    }
    try {
      const r = await api.patch(`/leads/${leadId}`, payload);
      setLead(r.data);
      setForm(pickEditable(r.data));
      toast.success("Lead details saved.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally { setSavingLead(false); }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    setSavingNote(true);
    try {
      const r = await api.post(`/leads/${leadId}/notes`, { body: note });
      setLead(r.data);
      setForm(pickEditable(r.data));
      setNote("");
      toast.success("Note added.");
    } catch { toast.error("Failed to add note."); }
    finally { setSavingNote(false); }
  };

  if (loadErr) {
    return (
      <div className="text-propertic-navy">
        <Link to="/admin/leads" className="inline-flex items-center gap-2 text-xs font-mono-brand text-propertic-muted hover:text-propertic-gold mb-6">
          <ArrowLeft className={`w-3.5 h-3.5 ${isRTL ? "rtl-mirror" : ""}`} /> Leads
        </Link>
        <div className="bg-white border hairline rounded-2xl p-8">{loadErr}</div>
      </div>
    );
  }
  if (!lead) return <div className="text-propertic-muted font-mono-brand">Loading…</div>;

  return (
    <div data-testid="admin-lead-detail">
      <Link to="/admin/leads" className="inline-flex items-center gap-2 text-xs font-mono-brand text-propertic-muted hover:text-propertic-gold mb-6">
        <ArrowLeft className={`w-3.5 h-3.5 ${isRTL ? "rtl-mirror" : ""}`} /> Leads
      </Link>
      <div className="grid lg:grid-cols-[1.4fr_1fr] gap-6">
        <div className="bg-white rounded-2xl border hairline p-6">
          <div className="flex items-start justify-between flex-wrap gap-3 mb-6">
            <div>
              <p className="font-mono-brand text-propertic-gold mb-1">{lead.source.toUpperCase()}</p>
              <h1 className="font-editorial text-3xl text-propertic-navy">{lead.full_name}</h1>
              <p className="text-propertic-ink/80 text-sm mt-1">{lead.email} · {lead.phone}</p>
              <p className="text-[11px] text-propertic-muted font-mono-brand mt-1">
                Created {new Date(lead.created_at).toLocaleString()} · Updated {new Date(lead.updated_at).toLocaleString()}
              </p>
            </div>
            <div className="min-w-[200px]">
              <p className="font-mono-brand text-propertic-muted mb-2 text-[10px] tracking-[0.14em] uppercase">{t("admin.status")}</p>
              <Select value={lead.status} onValueChange={setStatus}>
                <SelectTrigger data-testid="lead-status-select" className="bg-white border hairline text-propertic-navy h-10 rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white border hairline">
                  {LEAD_STATUSES.map((s) => (
                    <SelectItem key={s} value={s}>{LEAD_STATUS_LABEL[lang][s]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="mt-2">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-editorial text-xl text-propertic-navy">Lead details</h2>
              {dirty && (
                <div className="flex items-center gap-2">
                  <button
                    onClick={revert}
                    data-testid="lead-revert-btn"
                    className="h-9 px-3 rounded-full border hairline text-[10px] font-mono-brand tracking-[0.14em] uppercase text-propertic-muted hover:text-propertic-navy hover:border-propertic-navy transition-colors inline-flex items-center gap-1.5"
                  ><Undo2 className="w-3 h-3" /> Revert</button>
                </div>
              )}
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <TextField label="Interest" value={form.interest || ""} onChange={(v) => setField("interest", v)} placeholder="e.g. 2BR Downtown, off-plan" testid="lead-edit-interest" />
              <TextField label="Country of residence" value={form.country || ""} onChange={(v) => setField("country", v)} placeholder="e.g. United Kingdom" testid="lead-edit-country" />
              <NumberField label="Budget (AED)" value={form.budget_aed} onChange={(v) => setField("budget_aed", v)} placeholder="e.g. 2,000,000" testid="lead-edit-budget" prefix="AED" />
              <NumberField label="Loan amount (AED)" value={form.loan_amount_aed} onChange={(v) => setField("loan_amount_aed", v)} placeholder="e.g. 1,500,000" testid="lead-edit-loan" prefix="AED" />
              <NumberField label="Loan tenure (years)" value={form.loan_tenure_years} onChange={(v) => setField("loan_tenure_years", v)} placeholder="e.g. 25" testid="lead-edit-tenure" prefix="Yrs" integer />
              <NumberField label="Monthly income (AED)" value={form.monthly_income_aed} onChange={(v) => setField("monthly_income_aed", v)} placeholder="e.g. 45,000" testid="lead-edit-income" prefix="AED" />
              <SelectField label="Employment type" value={form.employment_type || ""} onChange={(v) => setField("employment_type", v)} options={EMPLOYMENT_OPTIONS} testid="lead-edit-employment" />
            </div>
            <div className="mt-5 flex items-center gap-3 flex-wrap">
              <button
                onClick={saveLead}
                disabled={!dirty || savingLead}
                data-testid="lead-save-btn"
                className="btn-gold rounded-full h-11 px-6 text-xs font-mono-brand tracking-widest disabled:opacity-40 inline-flex items-center gap-2"
              >
                <Save className="w-3.5 h-3.5" />
                {savingLead ? "Saving…" : (dirty ? "Save changes" : "No changes to save")}
              </button>
              {dirty && <span className="text-[11px] font-mono-brand text-amber-600">Unsaved changes</span>}
            </div>
          </div>

          {lead.message && (
            <div className="mt-8">
              <p className="font-mono-brand text-propertic-muted mb-2 text-[10px] tracking-[0.14em] uppercase">Message / Transcript</p>
              <pre className="whitespace-pre-wrap text-sm text-propertic-ink/85 bg-propertic-cream border hairline rounded-xl p-4 font-sans">{lead.message}</pre>
            </div>
          )}
        </div>

        <div className="bg-white rounded-2xl border hairline p-6">
          <h2 className="font-editorial text-2xl text-propertic-navy mb-4">{t("admin.notes")}</h2>
          <div className="space-y-3 max-h-72 overflow-y-auto mb-4">
            {(lead.notes || []).length === 0 && <p className="text-propertic-muted text-sm">No notes yet.</p>}
            {(lead.notes || []).map((n) => (
              <div key={n.id} className="border-s-2 border-propertic-gold ps-3 py-1" data-testid={`lead-note-${n.id}`}>
                <p className="text-sm text-propertic-ink/90 whitespace-pre-wrap">{n.body}</p>
                <p className="font-mono-brand text-propertic-muted mt-1 text-[10px]">{n.author} · {new Date(n.created_at).toLocaleString()}</p>
              </div>
            ))}
          </div>
          <textarea
            rows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            data-testid="lead-note-input"
            placeholder="Add a note… (e.g. Called at 3pm, asked for JVC townhouse options, follow up Monday)"
            className="w-full bg-white rounded-xl border hairline p-3 text-propertic-navy text-sm focus:border-propertic-gold outline-none transition-colors resize-none"
          />
          <button
            onClick={addNote}
            disabled={savingNote || !note.trim()}
            data-testid="lead-note-add-btn"
            className="mt-3 btn-gold rounded-full w-full h-10 text-xs font-mono-brand disabled:opacity-60"
          >
            {savingNote ? "…" : t("admin.add_note")}
          </button>
        </div>
      </div>
    </div>
  );
}

function TextField({ label, value, onChange, placeholder, testid }) {
  return (
    <label className="block">
      <span className="font-mono-brand text-propertic-muted text-[10px] tracking-[0.14em] uppercase">{label}</span>
      <input
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testid}
        className="mt-2 w-full bg-white rounded-xl border hairline h-11 px-4 text-propertic-navy text-sm focus:border-propertic-gold outline-none transition-colors"
      />
    </label>
  );
}

function NumberField({ label, value, onChange, placeholder, testid, prefix, integer = false }) {
  const display = value === null || value === undefined || value === "" ? "" : String(value);
  return (
    <label className="block">
      <span className="font-mono-brand text-propertic-muted text-[10px] tracking-[0.14em] uppercase">{label}</span>
      <div className="mt-2 flex items-stretch gap-0 border-b border-propertic-ink/20 focus-within:border-propertic-gold transition-colors">
        {prefix && (
          <span className="inline-flex items-center px-3 text-[10px] font-mono-brand tracking-[0.14em] uppercase text-propertic-muted select-none">{prefix}</span>
        )}
        <input
          value={display}
          onChange={(e) => {
            let v = e.target.value;
            if (integer) v = v.replace(/[^0-9]/g, "");
            else v = v.replace(/[^0-9.]/g, "");
            onChange(v);
          }}
          placeholder={placeholder}
          inputMode="numeric"
          data-testid={testid}
          className="flex-1 bg-transparent outline-none py-2 text-propertic-navy text-sm tabular"
        />
      </div>
    </label>
  );
}

function SelectField({ label, value, onChange, options, testid }) {
  return (
    <label className="block">
      <span className="font-mono-brand text-propertic-muted text-[10px] tracking-[0.14em] uppercase">{label}</span>
      <Select value={value || "__none"} onValueChange={(v) => onChange(v === "__none" ? "" : v)}>
        <SelectTrigger data-testid={testid} className="bg-white border hairline text-propertic-navy h-11 rounded-xl mt-2">
          <SelectValue placeholder="Select…" />
        </SelectTrigger>
        <SelectContent className="bg-white border hairline">
          <SelectItem value="__none"><span className="text-propertic-muted">—</span></SelectItem>
          {options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
        </SelectContent>
      </Select>
    </label>
  );
}
